import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPageTitle from '../components/AdminPageTitle';
import { fetchProducts as fetchProductsApi } from '../services/products';
import { getStorageUrl } from '../services/api';
import './Products.css';

const ITEMS_PER_PAGE = 10;

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProductsApi({
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
      });
      setProducts(data.data || []);
      setTotalPages(data.last_page ?? 1);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.response?.data?.message || 'Failed to load products.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Debounced search: when user types, reset to page 1 and refetch after a short delay
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getImageUrl = (product) => getStorageUrl(product?.image);

  const descriptionSnippet = (text, maxLen = 60) => {
    if (!text || typeof text !== 'string') return '—';
    const t = text.trim();
    return t.length <= maxLen ? t : t.slice(0, maxLen) + '…';
  };

  const variantCount = (product) => {
    const specs = product.specifications || product.variants;
    return Array.isArray(specs) ? specs.length : 0;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEdit = (productId) => {
    navigate(`/admin/products/${productId}/edit`);
  };

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return 'status-active';
    if (s === 'inactive') return 'status-inactive';
    return 'status-default';
  };

  return (
    <div className="admin-products-page">
      <AdminPageTitle title="Products" />
      <div className="page-header">
        <h1>Products Management</h1>
        <button 
          className="add-product-btn"
          onClick={() => navigate('/admin/products/new')}
        >
          + Add New Product
        </button>
      </div>

      <div className="products-filters">
        <div className="search-box">
          <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search by name or description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          className="status-filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {error && (
        <div className="products-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading products...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Description</th>
                  <th>Stock</th>
                  <th>Price (¥)</th>
                  <th>Variants</th>
                  <th>Featured</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">
                      {searchTerm || statusFilter ? 'No products match your filters.' : 'No products yet. Add your first product.'}
                    </td>
                  </tr>
                ) : (
                  products.map(product => (
                    <tr key={product.id}>
                      <td>
                        <div className="product-cell">
                          {getImageUrl(product) ? (
                            <img src={getImageUrl(product)} alt={product.name} />
                          ) : (
                            <div className="product-cell-placeholder">—</div>
                          )}
                          <span>{product.name}</span>
                        </div>
                      </td>
                      <td className="desc-cell">{descriptionSnippet(product.description)}</td>
                      <td>{product.stock != null ? product.stock : '—'}</td>
                      <td>{product.price != null ? `¥${Number(product.price).toLocaleString()}` : '—'}</td>
                      <td>{variantCount(product)}</td>
                      <td>
                        {product.featured ? (
                          <span className="featured-badge">Featured</span>
                        ) : (
                          <span className="featured-none">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`status-btn ${getStatusClass(product.status)}`}>
                          {(product.status || 'active').charAt(0).toUpperCase() + (product.status || 'active').slice(1)}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="action-btn edit-btn"
                          onClick={() => handleEdit(product.id)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && total > 0 && (
            <div className="pagination-info">
              Showing page {currentPage} of {totalPages} ({total} total)
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                  let page;
                  if (totalPages <= 10) page = i + 1;
                  else if (currentPage <= 5) page = i + 1;
                  else if (currentPage >= totalPages - 4) page = totalPages - 9 + i;
                  else page = currentPage - 5 + i;
                  return (
                    <button
                      key={page}
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Products;
